import React, { useEffect, useState } from "react";
import { StickyNote, Plus, Wrench, Truck, ClipboardList, CheckCircle2, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select, EmptyState } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const TYPE_META = {
  OPERATIONAL: { label: "Operational", icon: ClipboardList, tone: "info" },
  EQUIPMENT: { label: "Equipment", icon: Wrench, tone: "warn" },
  SUPPLY: { label: "Supply", icon: Truck, tone: "mute" },
};
const PRIORITY_TONE = { HIGH: "danger", NORMAL: "info", LOW: "mute" };
const FILTERS = [["open", "Open"], ["resolved", "Resolved"], ["all", "All"]];

export default function BranchNotes() {
  const toast = useToast();
  const { isRole } = useAuth();
  const { t: tr } = useT();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ note_type: "OPERATIONAL", title: "", message: "", priority: "NORMAL", photo: null });

  const canAdd = !isRole("OWNER");

  const load = () => {
    const qs = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/branch-notes${qs}`).then(({ data }) => setData(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const readPhoto = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, photo: reader.result }));
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.message.trim()) return toast.error(tr("A message is required"));
    setBusy(true);
    try {
      await api.post("/branch-notes", form);
      toast.success(tr("Note submitted"));
      setOpen(false);
      setForm({ note_type: "OPERATIONAL", title: "", message: "", priority: "NORMAL", photo: null });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Failed")); }
    finally { setBusy(false); }
  };

  const toggle = async (n) => {
    try { await api.post(`/branch-notes/${n.id}/resolve`); toast.success(tr("Updated")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || tr("Failed")); }
  };

  if (!data) return <PageLoader />;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title={tr("Branch Notes")} subtitle={tr("Operational, equipment & supply issues")} icon={StickyNote}>
        {canAdd && <Button onClick={() => setOpen(true)} data-testid="add-note-button"><Plus size={18} /> {tr("New Note")}</Button>}
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`notes-filter-${k}`}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-all ${filter === k ? "border-primary bg-primary text-white" : "border-line bg-white text-ink-soft hover:bg-stone-50"}`}>
            {tr(label)}{k === "open" && data.open_count > 0 && <span className={`ml-1.5 rounded-full px-1.5 text-[11px] ${filter === k ? "bg-white/25" : "bg-stone-100 text-ink-mute"}`}>{data.open_count}</span>}
          </button>
        ))}
      </div>

      {data.notes.length === 0 ? (
        <EmptyState title={tr("No notes here")} subtitle={tr("Log equipment problems, supply issues or operational notes so HQ can act on them.")} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.notes.map((n) => {
            const t = TYPE_META[n.note_type] || TYPE_META.OPERATIONAL;
            const Icon = t.icon;
            return (
              <Card key={n.id} className="flex flex-col p-4" data-testid={`note-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-light text-primary"><Icon size={16} /></div>
                    <div>
                      <Badge tone={t.tone}>{tr(t.label)}</Badge>
                      {n.branch_name && <p className="mt-1 text-[11px] text-ink-mute">{n.branch_name}</p>}
                    </div>
                  </div>
                  <Badge tone={PRIORITY_TONE[n.priority] || "mute"}>{tr(n.priority)}</Badge>
                </div>
                {n.title && <p className="mt-3 font-heading font-bold text-ink">{n.title}</p>}
                <p className="mt-1 flex-1 text-sm text-ink-soft">{n.message}</p>
                {n.photo && <img src={n.photo} alt="" className="mt-3 h-28 w-full rounded-xl object-cover" />}
                <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
                  <div className="min-w-0">
                    <p className="truncate text-xs text-ink-mute">{tr("By")} {n.created_by_name}</p>
                    <Badge tone={n.status === "resolved" ? "success" : "warn"} className="mt-1">{n.status === "resolved" ? tr("Resolved") : tr("Open")}</Badge>
                  </div>
                  {canAdd && (
                    <Button size="sm" variant={n.status === "resolved" ? "outline" : "success"} onClick={() => toggle(n)} data-testid={`note-toggle-${n.id}`}>
                      {n.status === "resolved" ? <><RotateCcw size={14} /> {tr("Reopen")}</> : <><CheckCircle2 size={14} /> {tr("Resolve")}</>}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={tr("New Branch Note")}
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{tr("Cancel")}</Button><Button onClick={submit} loading={busy} data-testid="submit-note">{tr("Submit")}</Button></>}>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <Select label={tr("Type")} value={form.note_type} onChange={(e) => setForm({ ...form, note_type: e.target.value })} data-testid="note-type">
            <option value="OPERATIONAL">{tr("Operational")}</option>
            <option value="EQUIPMENT">{tr("Equipment Issue")}</option>
            <option value="SUPPLY">{tr("Supply Issue")}</option>
          </Select>
          <Select label={tr("Priority")} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="note-priority">
            <option value="LOW">{tr("Low")}</option>
            <option value="NORMAL">{tr("Normal")}</option>
            <option value="HIGH">{tr("High")}</option>
          </Select>
        </div>
        <div className="mb-3"><label className="cf-label">{tr("Title (optional)")}</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="cf-input" data-testid="note-title" /></div>
        <div className="mb-3"><label className="cf-label">{tr("Message")}</label><textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="cf-input" placeholder={tr("Describe the issue or note...")} data-testid="note-message" /></div>
        <div><label className="cf-label">{tr("Photo (optional)")}</label><input type="file" accept="image/*" onChange={readPhoto} className="cf-input" data-testid="note-photo" />
          {form.photo && <img src={form.photo} alt="" className="mt-2 h-20 rounded-lg object-cover" />}</div>
      </Modal>
    </div>
  );
}
